import { GeofenceReviewDialog } from "@/features/time/geofence-review-dialog";
import { EntryCorrectionDialog, isCorrectableEntry } from "@/features/time/entry-correction-dialog";
// PHASE_1 / B2 — the admin PDKS (attendance/timesheet) dashboard Content
// component (ADR-014 §2).
//
// AdminPdksContent is a PURE, shell-agnostic Content component. It renders the
// manager's timesheet queue from GET /v1/time/timesheets via DataTable +
// useKeysetQuery (BUILD_PLAN §5 keyset, never OFFSET) with the approve / lock /
// reopen actions. Locking computes overtime server-side and emits the
// timesheet.locked + time_entry.locked events P2 payroll consumes — the lock
// response surfaces the OvertimeBreakdown (regular/ot1/ot2 hours) as a banner.
//
// Faz 8.6 / Batch A8 restyles the VISUAL layer to the novem PDKS language
// (NovemHR-ekranlar pdks/admin-*.jsx — serif PageHeader+em, a StatCard strip
// [regular hours / awaiting approval / approved / locked], a FilterPillBar
// status filter, a SearchInput, a novem table with Badge status tones, and a
// confirm step on the weighty lock/reopen transitions). The established
// submit→approve→lock + reopen contract, per-status action gating, and
// ScopeGate convenience checks remain intact; the ADR-018 entry quality queue
// below is a separate server-filtered path with its own bodyless approval.
//
// The status pill is a SERVER filter (GET /v1/time/timesheets?status=). The
// free-text search still narrows only the loaded window: the route has no text
// parameter. The KPI strip and pill counts read a separate UNFILTERED query
// and sum its LOADED window honestly (a "+" suffix flags more pages).
//
// The action buttons are hidden by <ScopeGate> off the active role — CONVENIENCE
// ONLY; the server re-checks every mutation (ADR-017; the frontend is never a
// trust boundary, ADR-008). The ADR-018 entry quality queue is separately
// server-filtered with requires_review=true; its bodyless approval route resolves
// the actor itself and rejects self-approval server-side. Its rows and the records
// feed rows open the manager correction modal (PATCH /v1/time/entries/:id,
// time_attendance.edit); this screen owns that modal's pending write.
//
// Hours are DECIMALS (`_hours`), never money — nothing here goes through <Money>
// (Phase 1 money/hours boundary). The hour columns are plain numeric strings.

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useBlocker } from "react-router";
import { type InfiniteData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Clock3,
  Hourglass,
  Lock,
  Search,
  Timer,
} from "lucide-react";

import {
  Badge,
  Banner,
  Button,
  DataTable,
  Empty,
  ErrorState,
  FilterPillBar,
  HelpHint,
  Loading,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PageHeader,
  ScopeGate,
  SearchInput,
  StatCard,
  type DataTableColumn,
  type FilterPillItem,
} from "@novemai/components";

import { EnvelopeError, errorMessage } from "@/api/errors";
import { type KeysetPage, useKeysetQuery } from "@/api/keyset";
import { useTimeZone } from "@/api/use-time-zone";
import { formatDateRange } from "@/lib/date-range";
import {
  approveTimeEntry,
  approveTimesheet,
  fetchReviewEntriesPage,
  fetchTimeCaller,
  fetchSchedulesPage,
  fetchTimesheetsPage,
  lockTimesheet,
  publishSchedule,
  reopenTimesheet,
  type OvertimePayload,
  type ScheduleRow,
  type TimeEntryRow,
  type TimesheetRow,
} from "@/features/time/time-api";
import {
  TIME_CAP_APPROVE,
  TIME_CAP_EDIT,
  TIME_CAP_LOCK,
  TIME_CAP_REOPEN,
  timeCapabilities,
} from "@/features/time/time-capabilities";
import { useEffectivePermissions } from "@/features/roles-permissions/use-effective-permissions";
import { formatCountWithMore } from "@/lib/count";
import { formatHours } from "@/lib/hours";
import { useTabParam } from "@/lib/use-tab-param";
import { useDebouncedSearchParam } from "@/lib/use-url-filters";
import { PersonLink } from "@/components/person-link";
import { MENU_SECTIONS } from "@/components/menu-config";
import { filterMenuByAuthority } from "@/components/use-visible-menu";
import { useScopeTier } from "@/api/use-scope-tier";
import { formatTimeInstant } from "@/features/time/format-instant";
import { OrphanedPunchesSection } from "@/features/time/orphaned-punches-section";
import { PdksPunchFeed } from "@/features/time/pdks-punch-feed";
import { TimesheetDaysDialog } from "@/features/time/timesheet-days-view";
import {
  ENTRY_STATUS_TONE,
  SCHEDULE_STATUS_TONE,
  TIMESHEET_STATUS_TONE,
} from "@/features/time/time-status";

/** The status filter pill values — "all" plus the timesheet lifecycle states. */
type StatusFilter = "all" | "open" | "submitted" | "approved" | "locked" | "reopened";

const STATUS_FILTERS: readonly StatusFilter[] = [
  "all",
  "open",
  "submitted",
  "approved",
  "locked",
  "reopened",
];

/** Which weighty transition a confirm dialog is open for (null = closed). */
type PendingConfirm = { readonly kind: "lock" | "reopen"; readonly id: string } | null;

type TimesheetTransitionKind = "approve" | "lock" | "reopen";

const TIMESHEET_QUEUE_QUERY_KEY = ["time", "timesheets"] as const;
const REVIEW_QUEUE_QUERY_KEY = ["time", "entries", "requires-review"] as const;
const SCHEDULE_QUEUE_QUERY_KEY = ["time", "schedules"] as const;

/**
 * The browser-owned immutable transition. A 5xx/network outcome can arrive
 * after the server committed an approval, reopen, or payroll-freezing lock;
 * its exact action, body, and key must therefore replay rather than mint a
 * second logical write.
 */
interface PendingTimesheetTransitionIntent {
  readonly fingerprint: string;
  readonly kind: TimesheetTransitionKind;
  readonly timesheetId: string;
  readonly body: {
    readonly reason?: string;
    readonly jurisdiction?: string;
  };
  readonly idempotencyKey: string;
}

/**
 * api_time_approve_entry has no request body. Keeping just this entry id + key
 * in the browser is deliberate: the server derives the caller and refuses a
 * self-owned entry, rather than accepting a client-supplied approver identity.
 */
interface PendingEntryApprovalIntent {
  readonly entryId: string;
  readonly idempotencyKey: string;
}

/** A retained, bodyless schedule publication intent. */
interface PendingSchedulePublishIntent {
  readonly scheduleId: string;
  readonly idempotencyKey: string;
}

function timesheetTransitionFingerprint(
  kind: TimesheetTransitionKind,
  timesheetId: string,
  body: PendingTimesheetTransitionIntent["body"],
): string {
  return JSON.stringify([kind, timesheetId, body.reason ?? null, body.jurisdiction ?? null]);
}

/** A known 4xx has a definitive server outcome; network/5xx remains ambiguous. */
function isDefinitiveTimeWriteFailure(error: unknown): boolean {
  return error instanceof EnvelopeError && error.httpStatus >= 400 && error.httpStatus < 500;
}

export function AdminPdksContent(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  const queryClient = useQueryClient();
  // Permission-derived (default-closed while loading): a transition button
  // that appears a beat late beats offering an action that will 403.
  const { capabilities: allowedCells } = useEffectivePermissions();
  const capabilities = timeCapabilities(allowedCells);
  const scopeTier = useScopeTier();
  // The rail's own filter over its manifest, so this link and the rail entry
  // cannot disagree. Schedules needs no page-level access gate.
  const canOpenSchedules = filterMenuByAuthority(MENU_SECTIONS, {
    capabilities: allowedCells,
    scopeTier,
    access: new Set(),
  }).some((section) => section.items.some((item) => item.key === "schedules"));

  // The last lock's overtime breakdown — surfaced as a banner so the manager
  // sees the regular/ot split the lock emitted to payroll. Decimal hours.
  const [geofencePending, setGeofencePending] = useState(false);
  const [geofenceEntry, setGeofenceEntry] = useState<TimeEntryRow | null>(null);
  const [correctionPending, setCorrectionPending] = useState(false);
  const [correctionEntry, setCorrectionEntry] = useState<TimeEntryRow | null>(null);
  const caller = useQuery({ queryKey: ["time", "caller"], queryFn: fetchTimeCaller });
  const [lastOvertime, setLastOvertime] = useState<OvertimePayload | null>(null);
  // Filter + search live in the URL (SEM-6350): the rows link to a person now,
  // and returning from one used to land on the unfiltered list.
  const [statusFilter, setStatusFilter] = useTabParam<StatusFilter>(
    "status",
    STATUS_FILTERS,
    "all",
  );
  const [search, setSearch] = useDebouncedSearchParam("q");
  // Lock + reopen go through a confirm step (weighty transitions). approve is a
  // direct inline action (the mockup's one-click approve).
  const [confirm, setConfirm] = useState<PendingConfirm>(null);
  // The sheet whose seven days are open. Read-only, so it needs no write intent
  // and never joins hasPendingWriteIntent.
  const [daysFor, setDaysFor] = useState<{ id: string; name: string | null } | null>(null);
  const [hasPendingTransitionIntent, setHasPendingTransitionIntent] = useState(false);
  const [hasPendingEntryApprovalIntent, setHasPendingEntryApprovalIntent] = useState(false);
  const [hasPendingSchedulePublishIntent, setHasPendingSchedulePublishIntent] = useState(false);
  const pendingTransition = useRef<PendingTimesheetTransitionIntent | null>(null);
  const pendingEntryApproval = useRef<PendingEntryApprovalIntent | null>(null);
  const pendingSchedulePublish = useRef<PendingSchedulePublishIntent | null>(null);
  const hasPendingWriteIntent =
    geofencePending ||
    correctionPending ||
    hasPendingTransitionIntent ||
    hasPendingEntryApprovalIntent ||
    hasPendingSchedulePublishIntent;

  // This screen owns each immutable time-write key. Letting the shell navigate
  // away after a network/5xx response would unmount that owner and make a retry
  // mint a new payroll-relevant transition or entry approval.
  const transitionBlocker = useBlocker(hasPendingWriteIntent);
  useEffect(() => {
    if (!hasPendingWriteIntent && transitionBlocker.state === "blocked") {
      transitionBlocker.reset();
    }
  }, [hasPendingWriteIntent, transitionBlocker]);

  // With the "all" pill both hooks share one cache entry, so nothing loads twice.
  const query = useKeysetQuery<TimesheetRow>({
    queryKey: [...TIMESHEET_QUEUE_QUERY_KEY, statusFilter],
    fetchPage: (cursor) =>
      fetchTimesheetsPage(cursor, statusFilter === "all" ? {} : { status: statusFilter }),
  });
  const summaryQuery = useKeysetQuery<TimesheetRow>({
    queryKey: [...TIMESHEET_QUEUE_QUERY_KEY, "all"],
    fetchPage: (cursor) => fetchTimesheetsPage(cursor),
  });
  const reviewQuery = useKeysetQuery<TimeEntryRow>({
    queryKey: REVIEW_QUEUE_QUERY_KEY,
    fetchPage: fetchReviewEntriesPage,
  });
  const schedulesQuery = useKeysetQuery<ScheduleRow>({
    queryKey: SCHEDULE_QUEUE_QUERY_KEY,
    fetchPage: fetchSchedulesPage,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: TIMESHEET_QUEUE_QUERY_KEY });
  };

  /** Entry approval can change both the review list and the timesheet queue. */
  const refreshEntryApprovalQueues = (): void => {
    void queryClient.invalidateQueries({ queryKey: REVIEW_QUEUE_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: TIMESHEET_QUEUE_QUERY_KEY });
  };

  const invalidateScheduleQueue = (): void => {
    void queryClient.invalidateQueries({ queryKey: SCHEDULE_QUEUE_QUERY_KEY });
  };

  const ensureTransitionIntent = (
    kind: TimesheetTransitionKind,
    timesheetId: string,
  ): PendingTimesheetTransitionIntent | null => {
    // The current UI has no reopen reason or lock jurisdiction controls, but
    // the body still belongs to the intent so a future control cannot silently
    // turn an ambiguous retry into a different request.
    const body: PendingTimesheetTransitionIntent["body"] = {};
    const fingerprint = timesheetTransitionFingerprint(kind, timesheetId, body);
    const current = pendingTransition.current;
    if (current !== null) {
      return current.fingerprint === fingerprint ? current : null;
    }
    if (pendingEntryApproval.current !== null || pendingSchedulePublish.current !== null) {
      return null;
    }
    const next: PendingTimesheetTransitionIntent = {
      fingerprint,
      kind,
      timesheetId,
      body,
      idempotencyKey: crypto.randomUUID(),
    };
    pendingTransition.current = next;
    setHasPendingTransitionIntent(true);
    return next;
  };

  const settleTransitionIntent = (intent: PendingTimesheetTransitionIntent): void => {
    if (pendingTransition.current?.fingerprint !== intent.fingerprint) return;
    pendingTransition.current = null;
    setHasPendingTransitionIntent(false);
  };

  const ensureEntryApprovalIntent = (
    entryId: string,
  ): PendingEntryApprovalIntent | null => {
    const current = pendingEntryApproval.current;
    if (current !== null) return current.entryId === entryId ? current : null;
    if (pendingTransition.current !== null || pendingSchedulePublish.current !== null) return null;

    const next: PendingEntryApprovalIntent = {
      entryId,
      idempotencyKey: crypto.randomUUID(),
    };
    pendingEntryApproval.current = next;
    setHasPendingEntryApprovalIntent(true);
    return next;
  };

  const settleEntryApprovalIntent = (intent: PendingEntryApprovalIntent): void => {
    if (pendingEntryApproval.current !== intent) return;
    pendingEntryApproval.current = null;
    setHasPendingEntryApprovalIntent(false);
  };

  const ensureSchedulePublishIntent = (
    scheduleId: string,
  ): PendingSchedulePublishIntent | null => {
    const current = pendingSchedulePublish.current;
    if (current !== null) return current.scheduleId === scheduleId ? current : null;
    if (pendingTransition.current !== null || pendingEntryApproval.current !== null) return null;

    const next: PendingSchedulePublishIntent = {
      scheduleId,
      idempotencyKey: crypto.randomUUID(),
    };
    pendingSchedulePublish.current = next;
    setHasPendingSchedulePublishIntent(true);
    return next;
  };

  const settleSchedulePublishIntent = (intent: PendingSchedulePublishIntent): void => {
    if (pendingSchedulePublish.current !== intent) return;
    pendingSchedulePublish.current = null;
    setHasPendingSchedulePublishIntent(false);
  };

  const rejectTransitionIntent = (
    error: unknown,
    intent: PendingTimesheetTransitionIntent,
  ): void => {
    if (!isDefinitiveTimeWriteFailure(error)) return;
    settleTransitionIntent(intent);
    // A definitive transition/state denial can make this loaded row stale.
    // Close the weighty dialog, then refresh before a new independent action.
    if (intent.kind !== "approve") setConfirm(null);
    invalidate();
  };

  const approve = useMutation({
    mutationFn: (intent: PendingTimesheetTransitionIntent) =>
      approveTimesheet(intent.timesheetId, intent.idempotencyKey),
    onSuccess: (_result, intent) => {
      settleTransitionIntent(intent);
      invalidate();
    },
    onError: rejectTransitionIntent,
  });
  const reopen = useMutation({
    mutationFn: (intent: PendingTimesheetTransitionIntent) =>
      reopenTimesheet(intent.timesheetId, intent.idempotencyKey, intent.body.reason),
    onSuccess: (_result, intent) => {
      settleTransitionIntent(intent);
      setConfirm(null);
      invalidate();
    },
    onError: rejectTransitionIntent,
  });
  const lock = useMutation({
    mutationFn: (intent: PendingTimesheetTransitionIntent) =>
      lockTimesheet(intent.timesheetId, intent.idempotencyKey, intent.body.jurisdiction),
    onSuccess: (result, intent) => {
      settleTransitionIntent(intent);
      setLastOvertime(result.overtime);
      setConfirm(null);
      invalidate();
    },
    onError: rejectTransitionIntent,
  });
  const approveEntry = useMutation({
    mutationFn: (intent: PendingEntryApprovalIntent) =>
      approveTimeEntry(intent.entryId, intent.idempotencyKey),
    onSuccess: (result, intent) => {
      settleEntryApprovalIntent(intent);
      // Remove the now-approved row at once, then revalidate BOTH queues.
      // The server is the source of truth: this is only a brief cache bridge
      // while the authoritative requires_review=true page refreshes.
      queryClient.setQueryData<
        InfiniteData<KeysetPage<TimeEntryRow>, string | null>
      >(REVIEW_QUEUE_QUERY_KEY, (previous) => {
        if (previous === undefined) return previous;
        return {
          ...previous,
          pages: previous.pages.map((page) => ({
            ...page,
            items: page.items.filter((row) => row.entry_id !== result.entry.entry_id),
          })),
        };
      });
      refreshEntryApprovalQueues();
    },
    onError: (error, intent) => {
      // A known 4xx (including backend self-exclusion) is definitive. It must
      // never retain the key or imply client role gating is authorization.
      if (!isDefinitiveTimeWriteFailure(error)) return;
      settleEntryApprovalIntent(intent);
      refreshEntryApprovalQueues();
    },
  });
  const publishScheduleMutation = useMutation({
    mutationFn: (intent: PendingSchedulePublishIntent) =>
      publishSchedule(intent.scheduleId, intent.idempotencyKey),
    onSuccess: (result, intent) => {
      settleSchedulePublishIntent(intent);
      // Reflect the returned server row immediately, then revalidate the
      // keyset list. The cache write is only a short bridge to authoritative
      // server state, never a local lifecycle authority.
      queryClient.setQueryData<
        InfiniteData<KeysetPage<ScheduleRow>, string | null>
      >(SCHEDULE_QUEUE_QUERY_KEY, (previous) => {
        if (previous === undefined) return previous;
        return {
          ...previous,
          pages: previous.pages.map((page) => ({
            ...page,
            items: page.items.map((row) =>
              row.schedule_id === result.schedule.schedule_id ? result.schedule : row,
            ),
          })),
        };
      });
      invalidateScheduleQueue();
    },
    onError: (error, intent) => {
      // A known 4xx (including a draft→published conflict) settles this
      // intent. Refresh before a new independent action; network/5xx keeps
      // the exact key alive for replay.
      if (!isDefinitiveTimeWriteFailure(error)) return;
      settleSchedulePublishIntent(intent);
      invalidateScheduleQueue();
    },
  });
  const busy =
    approve.isPending ||
    reopen.isPending ||
    lock.isPending ||
    approveEntry.isPending ||
    publishScheduleMutation.isPending;
  const actionError =
    approve.error ??
    reopen.error ??
    lock.error ??
    approveEntry.error ??
    publishScheduleMutation.error;

  /** While an outcome is ambiguous, only the same transition may replay. */
  const canRunTransition = (kind: TimesheetTransitionKind, timesheetId: string): boolean => {
    const current = pendingTransition.current;
    return (
      !busy &&
      pendingEntryApproval.current === null &&
      pendingSchedulePublish.current === null &&
      (current === null ||
        (current.kind === kind && current.timesheetId === timesheetId))
    );
  };

  /** An ambiguous entry approval may replay only against its original row. */
  const canRunEntryApproval = (entryId: string): boolean => {
    const current = pendingEntryApproval.current;
    return (
      !busy &&
      pendingTransition.current === null &&
      pendingSchedulePublish.current === null &&
      (current === null || current.entryId === entryId)
    );
  };

  /** An ambiguous schedule publication may replay only against its own row. */
  const canRunSchedulePublish = (scheduleId: string): boolean => {
    const current = pendingSchedulePublish.current;
    return (
      !busy &&
      pendingTransition.current === null &&
      pendingEntryApproval.current === null &&
      (current === null || current.scheduleId === scheduleId)
    );
  };

  const startTransition = (kind: TimesheetTransitionKind, timesheetId: string): void => {
    if (!canRunTransition(kind, timesheetId)) return;
    const intent = ensureTransitionIntent(kind, timesheetId);
    if (intent === null) return;
    if (kind === "approve") approve.mutate(intent);
    else if (kind === "lock") lock.mutate(intent);
    else reopen.mutate(intent);
  };

  const startEntryApproval = (entryId: string): void => {
    if (!canRunEntryApproval(entryId)) return;
    const intent = ensureEntryApprovalIntent(entryId);
    if (intent === null) return;
    approveEntry.mutate(intent);
  };

  const startSchedulePublish = (scheduleId: string): void => {
    if (!canRunSchedulePublish(scheduleId)) return;
    const intent = ensureSchedulePublishIntent(scheduleId);
    if (intent === null) return;
    publishScheduleMutation.mutate(intent);
  };

  const closeConfirm = (): void => {
    // Modal dismissals otherwise unmount the only visible retry affordance for
    // a committed-but-lost lock/reopen response.
    if (
      pendingTransition.current !== null ||
      pendingEntryApproval.current !== null ||
      pendingSchedulePublish.current !== null ||
      busy
    ) {
      return;
    }
    setConfirm(null);
  };

  // KPI strip — honest sums/counts over the LOADED keyset window. Hours are
  // decimal strings on the row; sum as floats only for the display total
  // (Number() — the figures are hours, never money minor units).
  const stats = useMemo(() => {
    let regular = 0;
    let pending = 0;
    let approved = 0;
    let locked = 0;
    for (const row of summaryQuery.items) {
      regular += Number(row.total_regular_hours) || 0;
      if (row.status === "submitted") pending += 1;
      else if (row.status === "approved") approved += 1;
      else if (row.status === "locked") locked += 1;
    }
    return { regular, pending, approved, locked };
  }, [summaryQuery.items]);
  const regularHoursValue = `${formatHours(stats.regular, i18n.language) ?? "0"}${
    summaryQuery.hasNextPage && stats.regular > 0 ? "+" : ""
  }`;

  // Search matches the employee name, the short employee id or the period
  // bounds over the loaded window only; the route takes no text parameter.
  const visibleRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === "") return query.items;
    return query.items.filter((row) =>
      `${row.employee_name ?? ""} ${row.employee_id} ${row.period_start} ${row.period_end}`
        .toLowerCase()
        .includes(needle),
    );
  }, [query.items, search]);

  // Per-status counts for the filter pills (over the unfiltered loaded window).
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const row of summaryQuery.items) counts[row.status] = (counts[row.status] ?? 0) + 1;
    return counts;
  }, [summaryQuery.items]);

  const filterItems: ReadonlyArray<FilterPillItem<StatusFilter>> = [
    { value: "all", label: t("time.pdks.filter.all"), count: summaryQuery.items.length },
    { value: "open", label: t("time.pdks.filter.open"), count: statusCounts["open"] ?? 0 },
    {
      value: "submitted",
      label: t("time.pdks.filter.submitted"),
      count: statusCounts["submitted"] ?? 0,
    },
    {
      value: "approved",
      label: t("time.pdks.filter.approved"),
      count: statusCounts["approved"] ?? 0,
    },
    { value: "locked", label: t("time.pdks.filter.locked"), count: statusCounts["locked"] ?? 0 },
    {
      value: "reopened",
      label: t("time.pdks.filter.reopened"),
      count: statusCounts["reopened"] ?? 0,
    },
  ];

  const columns: readonly DataTableColumn<TimesheetRow>[] = [
    {
      id: "employee",
      header: t("time.column.employee"),
      // Same treatment as the review queue below: the id prefix is only the
      // FALLBACK for a tombstoned subject (ADR-011), which legitimately has no
      // name. Without this column a queue of other people's timesheets could
      // not say whose each one was (E26).
      cell: (row): React.ReactNode => (
        <PersonLink employeeId={row.employee_id}>
          {row.employee_name == null ? (
            <span className="font-mono text-[12px] text-[var(--fg-secondary)]">
              {row.employee_id.slice(0, 8)}
            </span>
          ) : (
            <span>{row.employee_name}</span>
          )}
        </PersonLink>
      ),
    },
    {
      id: "period",
      header: t("time.column.period"),
      // Calendar dates (date-only) — UTC-pinned via formatDateRange so the day
      // doesn't shift west of Greenwich (NOT a real instant).
      cell: (row): string => formatDateRange(i18n.language, row.period_start, row.period_end),
    },
    {
      id: "regular",
      header: t("time.column.regular_hours"),
      align: "end",
      // Decimal hours — NOT money. The API sends "40.00"; the locale decides the
      // separator (Turkish: comma).
      cell: (row): React.ReactNode => (
        <span className="font-mono tabular-nums text-[var(--fg-primary)]">
          {formatHours(row.total_regular_hours, i18n.language) ?? row.total_regular_hours}
        </span>
      ),
    },
    {
      id: "ot",
      header: t("time.column.ot_hours"),
      align: "end",
      cell: (row): React.ReactNode => (
        <span className="font-mono tabular-nums text-[var(--fg-secondary)]">
          {formatHours(row.total_ot_hours, i18n.language) ?? row.total_ot_hours}
        </span>
      ),
    },
    {
      id: "leave",
      header: t("time.column.leave_hours"),
      align: "end",
      // Approved leave drawn from the leave ledger — it counts toward the week
      // even though nobody punched it (LT-02).
      cell: (row): React.ReactNode => (
        <span className="font-mono tabular-nums text-[var(--fg-secondary)]">
          {formatHours(row.total_pto_hours, i18n.language) ?? row.total_pto_hours}
        </span>
      ),
    },
    {
      id: "status",
      header: t("time.column.status"),
      cell: (row): React.ReactNode => (
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge tone={TIMESHEET_STATUS_TONE[row.status] ?? "neutral"}>
            {t(`time.timesheet_status.${row.status}`)}
          </Badge>
          {row.provisional ? (
            <Badge tone="neutral">{t("time.days.provisional")}</Badge>
          ) : null}
          {row.leave_day_worked_days > 0 ? (
            <Badge tone="danger">{t("time.days.leave_day_worked")}</Badge>
          ) : null}
        </div>
      ),
    },
    {
      id: "actions",
      header: t("time.column.actions"),
      align: "end",
      cell: (row): React.ReactNode => (
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(): void => {
              setDaysFor({ id: row.timesheet_id, name: row.employee_name ?? null });
            }}
          >
            {t("time.days.view")}
          </Button>
          {row.status === "submitted" && (
            <ScopeGate required={TIME_CAP_APPROVE} allowed={capabilities}>
              <Button
                variant="primary"
                size="sm"
                disabled={!canRunTransition("approve", row.timesheet_id)}
                onClick={(): void => {
                  startTransition("approve", row.timesheet_id);
                }}
              >
                {t("time.action.approve")}
              </Button>
            </ScopeGate>
          )}
          {row.status === "approved" && (
            <ScopeGate required={TIME_CAP_LOCK} allowed={capabilities}>
              <Button
                variant="primary"
                size="sm"
                disabled={!canRunTransition("lock", row.timesheet_id)}
                onClick={(): void => {
                  if (canRunTransition("lock", row.timesheet_id)) {
                    setConfirm({ kind: "lock", id: row.timesheet_id });
                  }
                }}
              >
                {t("time.action.lock")}
              </Button>
            </ScopeGate>
          )}
          {(row.status === "approved" || row.status === "locked") && (
            <ScopeGate required={TIME_CAP_REOPEN} allowed={capabilities}>
              <Button
                variant="ghost"
                size="sm"
                disabled={!canRunTransition("reopen", row.timesheet_id)}
                onClick={(): void => {
                  if (canRunTransition("reopen", row.timesheet_id)) {
                    setConfirm({ kind: "reopen", id: row.timesheet_id });
                  }
                }}
              >
                {t("time.action.reopen")}
              </Button>
            </ScopeGate>
          )}
        </div>
      ),
    },
  ];

  /** Shared by the review queue and the records feed; null when not offered. */
  const renderCorrectionAction = (row: TimeEntryRow): React.ReactNode =>
    isCorrectableEntry(row) && capabilities.has(TIME_CAP_EDIT) ? (
      <Button
        variant="ghost"
        size="sm"
        disabled={busy || hasPendingWriteIntent}
        onClick={(): void => {
          setCorrectionEntry(row);
        }}
      >
        {t("time.pdks.correction.open")}
      </Button>
    ) : null;

  const reviewColumns: readonly DataTableColumn<TimeEntryRow>[] = [
    {
      id: "employee",
      header: t("time.column.employee"),
      // The id prefix stays as the FALLBACK, not the default: a tombstoned
      // subject legitimately has no name (ADR-011), and a manager still has to
      // be able to tell two rows apart. Named rows drop the mono treatment —
      // it was there to signal "this is an identifier", which a name is not.
      cell: (row): React.ReactNode => (
        <PersonLink employeeId={row.employee_id}>
          {row.employee_name === null ? (
            <span className="font-mono text-[12px] text-[var(--fg-secondary)]">
              {row.employee_id.slice(0, 8)}
            </span>
          ) : (
            <span>{row.employee_name}</span>
          )}
        </PersonLink>
      ),
    },
    {
      id: "start",
      header: t("time.column.start"),
      cell: (row): string => formatTimeInstant(i18n.language, timeZone, row.start_at),
    },
    {
      id: "source",
      header: t("time.column.source"),
      cell: (row): string =>
        t(`time.source.${row.source}`),
    },
    {
      id: "status",
      header: t("time.column.status"),
      cell: (row): React.ReactNode => (
        <Badge tone={ENTRY_STATUS_TONE[row.status] ?? "neutral"}>
          {t(`time.entry_status.${row.status}`)}
        </Badge>
      ),
    },
    {
      id: "review",
      header: t("time.pdks.review.column.quality"),
      cell: (row): React.ReactNode => (
        <div className="space-y-2">
          <Badge tone="warning">{t("time.pdks.review.quality_flag")}</Badge>
          {row.geofence_match ? <Button size="sm" variant="secondary" disabled={busy} onClick={() => setGeofenceEntry(row)}>{t("time.geofence.view")}</Button> : null}
          {row.geofence_match?.clock_in?.reason_required || row.geofence_match?.clock_out?.reason_required ? <p className="text-sm text-[var(--fg-secondary)]">{t("time.geofence.reason_required")}</p> : null}
        </div>
      ),
    },
    {
      id: "actions",
      header: t("time.column.actions"),
      align: "end",
      cell: (row): React.ReactNode => {
        const canApprove = row.status === "submitted" && capabilities.has(TIME_CAP_APPROVE);
        const canCorrect = isCorrectableEntry(row) && capabilities.has(TIME_CAP_EDIT);
        if (!canApprove && !canCorrect) {
          return <span className="text-[var(--fg-muted)]">{t("time.cell.dash")}</span>;
        }
        return (
          <div className="flex justify-end gap-2">
            {renderCorrectionAction(row)}
            {canApprove ? (
              <Button
                variant="primary"
                size="sm"
                disabled={!canRunEntryApproval(row.entry_id) || row.geofence_match?.clock_in?.reason_required === true || row.geofence_match?.clock_out?.reason_required === true}
                onClick={(): void => {
                  startEntryApproval(row.entry_id);
                }}
              >
                {t("time.action.approve")}
              </Button>
            ) : null}
          </div>
        );
      },
    },
  ];

  const scheduleColumns: readonly DataTableColumn<ScheduleRow>[] = [
    {
      id: "name",
      header: t("time.pdks.schedule.column.name"),
      cell: (row): string => row.name,
    },
    {
      id: "scope",
      header: t("time.pdks.schedule.column.scope"),
      cell: (row): string =>
        t(`time.pdks.schedule.scope.${row.scope_kind}`),
    },
    {
      id: "period",
      header: t("time.pdks.schedule.column.period"),
      cell: (row): string => formatDateRange(i18n.language, row.period_start, row.period_end),
    },
    {
      id: "status",
      header: t("time.pdks.schedule.column.status"),
      cell: (row): React.ReactNode => (
        <Badge tone={SCHEDULE_STATUS_TONE[row.status] ?? "neutral"}>
          {t(`time.schedule_status.${row.status}`)}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: t("time.column.actions"),
      align: "end",
      cell: (row): React.ReactNode =>
        row.status === "draft" ? (
          <ScopeGate required={TIME_CAP_EDIT} allowed={capabilities}>
            <Button
              variant="primary"
              size="sm"
              disabled={!canRunSchedulePublish(row.schedule_id)}
              onClick={(): void => {
                startSchedulePublish(row.schedule_id);
              }}
            >
              {t("time.action.publish_schedule")}
            </Button>
          </ScopeGate>
        ) : (
          <span className="text-[var(--fg-muted)]">{t("time.cell.dash")}</span>
        ),
    },
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader
        title={
          <>
            {t("time.pdks.title_lead")} <em>{t("time.pdks.title_em")}</em>
          </>
        }
        description={t("time.pdks.lead")}
      />

      {/* KPI strip — novem StatCard row (regular hrs / pending / approved / locked) */}
      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <StatCard
          label={t("time.pdks.stat.regular")}
          value={regularHoursValue}
          delta={t("time.pdks.stat.regular_sub")}
          deltaDirection="flat"
          tone="accent"
          icon={<Clock3 size={18} />}
        />
        <StatCard
          label={t("time.pdks.stat.pending")}
          value={formatCountWithMore(stats.pending, summaryQuery.hasNextPage)}
          delta={t("time.pdks.stat.pending_sub")}
          deltaDirection="flat"
          tone="warning"
          icon={<Hourglass size={18} />}
        />
        <StatCard
          label={t("time.pdks.stat.approved")}
          value={formatCountWithMore(stats.approved, summaryQuery.hasNextPage)}
          delta={t("time.pdks.stat.approved_sub")}
          deltaDirection="flat"
          tone="success"
          icon={<Timer size={18} />}
        />
        <StatCard
          label={t("time.pdks.stat.locked")}
          value={formatCountWithMore(stats.locked, summaryQuery.hasNextPage)}
          delta={t("time.pdks.stat.locked_sub")}
          deltaDirection="flat"
          tone="info"
          icon={<Lock size={18} />}
        />
      </div>

      {lastOvertime !== null && (
        <Banner severity="success" title={t("time.pdks.locked_title")}>
          <span className="inline-flex items-center gap-1">
            {t("time.pdks.locked_summary", {
              regular: lastOvertime.regular_hours,
              ot1: lastOvertime.ot1_hours,
              ot2: lastOvertime.ot2_hours,
            })}
            <HelpHint
              label={t("common.help_for", { field: t("time.pdks.locked_title") })}
            >
              {t("time.pdks.locked_summary_help")}
            </HelpHint>
          </span>
        </Banner>
      )}

      {actionError != null && (
        <Banner severity="danger" title={t("time.pdks.action_error_title")}>
          {errorMessage(actionError, t)}
        </Banner>
      )}

      <PdksPunchFeed rowAction={renderCorrectionAction} />

      {capabilities.has(TIME_CAP_EDIT) ? <OrphanedPunchesSection /> : null}

      <section aria-labelledby="time-review-queue-heading" className="space-y-3">
        <div>
          <h2
            id="time-review-queue-heading"
            className="font-serif text-[20px] text-[var(--fg-primary)]"
          >
            {t("time.pdks.review.title")}
          </h2>
          <p className="mt-1 font-sans text-[14px] leading-relaxed text-[var(--fg-secondary)]">
            {t("time.pdks.review.description")}
          </p>
        </div>

        {reviewQuery.isLoading ? (
          <Loading label={t("time.pdks.review.loading")} rows={3} />
        ) : reviewQuery.isError ? (
          <ErrorState
            title={t("time.pdks.review.error_title")}
            description={errorMessage(reviewQuery.error, t)}
          />
        ) : reviewQuery.items.length > 0 ? (
          <DataTable<TimeEntryRow>
            caption={t("time.pdks.review.table_caption")}
            columns={reviewColumns}
            rows={reviewQuery.items}
            rowKey={(row): string => row.entry_id}
            virtualize
            hasNextPage={reviewQuery.hasNextPage}
            onLoadMore={reviewQuery.fetchNextPage}
            isFetchingNextPage={reviewQuery.isFetchingNextPage}
            loadMoreLabel={t("time.load_more")}
          />
        ) : (
          <Empty
            title={t("time.pdks.review.empty_title")}
            description={t("time.pdks.review.empty_description")}
          />
        )}
      </section>

      {/* Shift design lives on /settings/schedules (its own rail entry); this
          section only publishes drafts, and links there for whoever the rail
          shows that entry to. */}
      <section aria-labelledby="time-schedule-publication-heading" className="space-y-3">
        <div>
          <h2
            id="time-schedule-publication-heading"
            className="font-serif text-[20px] text-[var(--fg-primary)]"
          >
            {t("time.pdks.schedule.title")}
          </h2>
          <p className="mt-1 font-sans text-[14px] leading-relaxed text-[var(--fg-secondary)]">
            {t("time.pdks.schedule.description")}{" "}
            {canOpenSchedules ? (
              <RouterLink
                to="/settings/schedules"
                className="text-[var(--accent)] no-underline hover:underline"
              >
                {t("time.pdks.schedule.design_link")}
              </RouterLink>
            ) : null}
          </p>
        </div>

        {schedulesQuery.isLoading ? (
          <Loading label={t("time.pdks.schedule.loading")} rows={3} />
        ) : schedulesQuery.isError ? (
          <ErrorState
            title={t("time.pdks.schedule.error_title")}
            description={errorMessage(schedulesQuery.error, t)}
          />
        ) : schedulesQuery.items.length > 0 ? (
          <DataTable<ScheduleRow>
            caption={t("time.pdks.schedule.table_caption")}
            columns={scheduleColumns}
            rows={schedulesQuery.items}
            rowKey={(row): string => row.schedule_id}
            virtualize
            hasNextPage={schedulesQuery.hasNextPage}
            onLoadMore={schedulesQuery.fetchNextPage}
            isFetchingNextPage={schedulesQuery.isFetchingNextPage}
            loadMoreLabel={t("time.load_more")}
          />
        ) : (
          <Empty
            title={t("time.pdks.schedule.empty_title")}
            description={t("time.pdks.schedule.empty_description")}
          />
        )}
      </section>

      {query.isLoading ? (
        <Loading label={t("time.pdks.loading")} rows={5} />
      ) : query.isError ? (
        <ErrorState
          title={t("time.pdks.error_title")}
          description={errorMessage(query.error, t)}
        />
      ) : query.items.length > 0 ? (
        <>
          {/* Filter bar — status pills + search (client-side over loaded window) */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <FilterPillBar<StatusFilter>
              aria-label={t("time.pdks.filter.label")}
              items={filterItems}
              value={statusFilter}
              onChange={setStatusFilter}
            />
            <SearchInput
              aria-label={t("time.pdks.search_label")}
              placeholder={t("time.pdks.search_placeholder")}
              icon={<Search size={15} />}
              value={search}
              onChange={(e): void => setSearch(e.target.value)}
              className="min-w-[240px]"
            />
          </div>

          {visibleRows.length > 0 ? (
            // The review queue and the schedules table above are data-tables too,
            // and the tour's generic `table` anchor takes the FIRST one: without
            // this override the timesheet step would frame the wrong table.
            <div data-tour="table">
              <DataTable<TimesheetRow>
                caption={t("time.pdks.table_caption")}
                columns={columns}
                rows={visibleRows}
                rowKey={(row): string => row.timesheet_id}
                virtualize
                hasNextPage={query.hasNextPage}
                onLoadMore={query.fetchNextPage}
                isFetchingNextPage={query.isFetchingNextPage}
                loadMoreLabel={t("time.load_more")}
              />
            </div>
          ) : (
            <Empty
              title={t("time.pdks.empty_filtered_title")}
              description={t("time.pdks.empty_filtered_description")}
            />
          )}
        </>
      ) : (
        <Empty title={t("time.pdks.empty_title")} description={t("time.pdks.empty_description")} />
      )}

      {/* Confirm dialog — a response-loss retry keeps its immutable lock/reopen
          intent visible. Escape, scrim, and Cancel cannot discard it. */}
      {daysFor !== null ? (
        <TimesheetDaysDialog
          timesheetId={daysFor.id}
          employeeName={daysFor.name}
          onClose={(): void => setDaysFor(null)}
        />
      ) : null}
      {geofenceEntry ? <GeofenceReviewDialog onPendingChange={setGeofencePending} entryId={geofenceEntry.entry_id} mode={geofenceEntry.employee_id !== caller.data?.employee_id && caller.data?.employee_id && capabilities.has(TIME_CAP_APPROVE) && ["active", "submitted"].includes(geofenceEntry.status) ? "supervisor_override" : "read"} onClose={() => setGeofenceEntry(null)} /> : null}
      {correctionEntry !== null ? (
        <EntryCorrectionDialog
          entry={correctionEntry}
          timeZone={timeZone}
          onPendingChange={setCorrectionPending}
          onClose={(): void => setCorrectionEntry(null)}
        />
      ) : null}
      <Modal
        open={confirm !== null}
        onClose={closeConfirm}
        closeOnEscape={!hasPendingWriteIntent && !busy}
        closeOnScrimClick={!hasPendingWriteIntent && !busy}
      >
        <ModalHeader
          title={
            confirm?.kind === "reopen"
              ? t("time.pdks.confirm.reopen_title")
              : t("time.pdks.confirm.lock_title")
          }
        />
        <ModalBody>
          <p className="font-sans text-[14px] leading-relaxed text-[var(--fg-secondary)]">
            {confirm?.kind === "reopen"
              ? t("time.pdks.confirm.reopen_body")
              : t("time.pdks.confirm.lock_body")}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="secondary"
            size="sm"
            onClick={closeConfirm}
            disabled={hasPendingWriteIntent || busy}
          >
            {t("time.pdks.confirm.cancel")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={
              confirm === null ||
              !canRunTransition(confirm.kind, confirm.id)
            }
            onClick={(): void => {
              if (confirm === null) return;
              startTransition(confirm.kind, confirm.id);
            }}
          >
            {confirm?.kind === "reopen"
              ? t("time.pdks.confirm.reopen_confirm")
              : t("time.pdks.confirm.lock_confirm")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
